import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import './PullToRefresh.css';

const PullToRefresh = ({ 
  children, 
  onRefresh, 
  isRefreshing = false, 
  pullThreshold = 80,
  maxPull = 120,
  disabled = false 
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef(null);
  const animationFrame = useRef(null);

  useEffect(() => {
    if (!isRefreshing && shouldRefresh) {
      handleRefresh();
    }
  }, [isRefreshing, shouldRefresh]);

  const handleTouchStart = (e) => {
    if (disabled || isRefreshing) return;
    
    // Only allow pull-to-refresh at the top of the scroll
    const container = containerRef.current;
    if (container && container.scrollTop > 0) return;
    
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    setIsPulling(true);
  };

  const handleTouchMove = (e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;
    
    // Only allow downward pull
    if (deltaY < 0) return;
    
    // Check if we're still at the top
    const container = containerRef.current;
    if (container && container.scrollTop > 0) {
      setIsPulling(false);
      resetPull();
      return;
    }
    
    // Apply resistance to make it feel natural
    const resistance = deltaY > pullThreshold ? 0.5 : 0.8;
    const clampedDelta = Math.min(deltaY * resistance, maxPull);
    
    setPullDistance(clampedDelta);
    setShouldRefresh(clampedDelta >= pullThreshold);
  };

  const handleTouchEnd = () => {
    if (!isPulling) return;
    
    setIsPulling(false);
    
    if (pullDistance >= pullThreshold && !isRefreshing) {
      setShouldRefresh(true);
      // Snap to threshold position
      setPullDistance(pullThreshold);
    } else {
      resetPull();
    }
  };

  const handleRefresh = async () => {
    try {
      await onRefresh();
    } finally {
      resetPull();
      setShouldRefresh(false);
    }
  };

  const resetPull = () => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    animationFrame.current = requestAnimationFrame(() => {
      setPullDistance(0);
    });
  };

  const pullProgress = Math.min(pullDistance / pullThreshold, 1);
  const iconRotation = pullProgress * 360;

  return (
    <div 
      className={`pull-to-refresh-container ${isPulling ? 'pulling' : ''} ${shouldRefresh ? 'refreshing' : ''}`}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div 
        className="pull-indicator"
        style={{ 
          height: `${pullDistance}px`,
          opacity: pullDistance > 0 ? 1 : 0
        }}
      >
        <div className="pull-content">
          <div 
            className="pull-icon"
            style={{ 
              transform: `rotate(${iconRotation}deg)`,
              opacity: shouldRefresh ? 1 : pullProgress
            }}
          >
            <RefreshCw size={24} />
          </div>
          <div className="pull-text">
            {isRefreshing ? (
              <span className="refreshing-text">Refreshing...</span>
            ) : shouldRefresh ? (
              <span className="release-text">Release to refresh</span>
            ) : pullDistance > 0 ? (
              <span className="pull-text-content">Pull to refresh</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pull-content-wrapper">
        {children}
      </div>

      {/* Loading overlay */}
      {isRefreshing && (
        <div className="refresh-overlay">
          <div className="refresh-spinner">
            <RefreshCw size={32} className="spinning" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PullToRefresh;
